import { useState, useEffect, useRef } from 'react'
import './MessageResponsesSection.scoped.sass'
import NewMessageReplyForm from '@/pages/HomePage/FeedContent/Thread/CardMainSection/NewMessageReplyForm'
import { MessageHTML } from '@/components/commons/MessageHTML'
import { HumanTime } from '@/components/commons/HumanTime'
import NameSlugPlaceholder from '../MainSectionHeader/NameSlugPlaceholder'
import { MessageAttachmentsSection } from '../MessageAttachmentsSection/MessageAttachmentsSection'
import threadsService from '@/app-domains/threads/threads.service'
import { MessageModal } from '@/components/WritingMessageModal'
import { ResponsesSectionHeader } from '../../ResponsesSectionHeader'
import { fullCleanMessageHtml } from '@/helpers/html'
import messagesService from '@/app-domains/messages/messages.service'
import { ReplyToResponse } from './ReplyToResponse/ReplyToResponse'
import classNames from 'classnames'
import useRenderEvent from '@/core/hooks/useRenderEvent'
import contactsService from '@/app-domains/contacts/contacts.service'

function transformMessagesIntoArray (messages) {
  return !Array.isArray(messages) ? [messages] : messages
}

export function MessageResponsesSection (props) {
  const [thread, setThread] = useState(props.thread)
  const [responses, setResponses] = useState(transformMessagesIntoArray(props.thread.messages))
  const [showAll, setShowAll] = useState(false)
  const message = thread.messages.filter((message) => message.isHeadOfThread)[0]

  function getThreadReplies (replies) {
    if (!showAll) return [...replies].sort((a, b) => new Date(b.dateRaw) - new Date(a.dateRaw)).slice(0, 1)
    return [...replies].sort((a, b) => new Date(a.dateRaw) - new Date(b.dateRaw))
  }

  useRenderEvent([`THREAD:${props.thread.composedid}:UPDATE:REPLY_ADDED`], (thread) => {
    setThread(thread)
    setResponses(transformMessagesIntoArray(thread.messages))
  })

  useEffect(() => {
    setThread(props.thread)
    setResponses(transformMessagesIntoArray(props.thread.messages))
  }, [props.thread])

  return <>
    <ResponsesSectionHeader responsesCount={thread.messages.length - 1} showMoreButtonEvent={() => { setShowAll(!showAll) }}/>
    <div className="responsesContainer">
      <div className="messageResponsesContainer">
        { thread.messages.length > 1 && message.repliable && <NewMessageReplyForm borderBottom={true} thread={thread} message={message}/> }
        { getThreadReplies(responses || [])
          .map((response, index) => {
            if (!threadsService.isMessageTheRootMessageOfTheThread(response, thread)) {
              return <MessageResponse key={index} response={response} thread={thread}/>
            } else {
              return getThreadReplies(response.subthread?.messages || [])
              // .sort((a, b) => new Date(b.dateRaw) - new Date(a.dateRaw))
                .map(message =>
                  <MessageResponse key={index} response={message} thread={response.subthread} isReplyToHead={true} isSubthreadReply={true}/>
                ) || []
            }
          })
        }
      </div>
    </div>
  </>
}

function GenericProfileImage ({ fromEmailOnlyName, fromEmail }) {
  const extractedDomainFromEmail = fromEmail.split('@')[1]
  return <div className="-img">
    { fromEmail && extractedDomainFromEmail !== 'gmail.com' && <img src={`https://${extractedDomainFromEmail}/favicon.ico`} className="senderInformationPhotoStyle"></img> }
    { fromEmailOnlyName && <NameSlugPlaceholder name={fromEmailOnlyName}/> }
    { !fromEmailOnlyName && <img src="https://via.placeholder.com/40" className="senderInformationPhotoStyle"></img> }
  </div>
}

function ProfileImage ({ contact }) {
  return <div className="-img" onClick={() => contactsService.travelToContactViewAndFocusContact(contact)}>
    { contact?.contactimageuri
      ? <img className='contact-image-img' src={contact.contactimageuri}/>
      : <NameSlugPlaceholder name={
          contactsService.buildContactFullname(contact)
      }/>}
  </div>
}

function MessageResponse (props) {
  const { response, thread } = props
  const [fullSize, setFullSize] = useState(true)
  const [showFullSizeButton, setShowFullSizeButton] = useState(false)
  const [needsShowMore, setNeedsShowMore] = useState(false)
  const messageElement = useRef(null)
  const [contact] = useState(contactsService.findContactByEmail(response.fromEmail) || contactsService.getPersonalInfoIfMailComesFromCustomer(response.fromEmail))
  const [messageModalReplyOpened, setMessageModalReplyOpened] = useState(false)

  const getFullSizeButton = () => {
    const responseContainer = document.querySelector(`.-rendered-response${response.messageid}`)
    if (responseContainer?.clientHeight > 150) {
      setNeedsShowMore(true)
      setShowFullSizeButton(true)
      setFullSize(false)
    }
  }

  useEffect(() => {
    getFullSizeButton()
  }, [])
  
  function toggleShowMore () {
    if (!fullSize && response.unopen) {
      messagesService.markMessageAsRead(response.id, thread.composedid, thread.boxId)
    }
    setFullSize(!fullSize)
  }

  const text = fullCleanMessageHtml(response.html || response.text)
  const html = fullCleanMessageHtml(response.html || response.text)

  const { fromEmailOnlyName, fromEmail, date, tos, attachments } = response

  return <div className={props.isSubthreadReply ? 'c-Subreply' : ''} key={response.messageid}>
    <div ref={messageElement} className={classNames('c-MessageResponsesSection', {
      subreply: props.isSubthreadReply,
      'has-responses': response.subthread?.messages
    })}>
      <div className='response-timeline' />
      <div className="-sender">
        <>{contact ? <ProfileImage contact={contact}/> : <GenericProfileImage fromEmail={fromEmail} fromEmailOnlyName={fromEmailOnlyName}/>}</>

        <div className="-details">
          <div className="-name">{fromEmailOnlyName} <i className="-from-email">{`<${fromEmail}>`}</i></div>
          <div className="-to">
            To: {tos ? tos.join(', ') : ''}
          </div>
        </div>
        <div className="-time">
          <HumanTime date={date}/>
        </div>
      </div>
      <div __message={response.id} className={`-rendered-response ${fullSize ? 'full-size' : ''} ${needsShowMore ? 'needs_show_more' : 'not_needs_show_more'} -rendered-response${response.messageid}`}>
        {
        html
          ? <div className="-content _html"><MessageHTML html={html}/></div>
          : <div className="-content _text">
            <pre>{text}</pre>
          </div>
      }
      </div>
      {showFullSizeButton ? <a className="-see_more" onClick={toggleShowMore}>{fullSize ? 'Read Less' : 'Read More'}</a> : ''}
      <div className="-rendered-attachment">
        <MessageAttachmentsSection attachments={attachments} skipLastBar={true}/>
      </div>
      <ReplyToResponse thread={thread} response={response} />
      { !props.isReplyToHeadMessage && response.subthread?.messages &&
      [...response.subthread?.messages]
        .sort((a, b) => new Date(a.dateRaw) - new Date(b.dateRaw)).map(message =>
          <div key={message.id}>
            <MessageResponse key={message.messageid} response={message} thread={response.subthread} isSubthreadReply={true}/>
          </div>
        )
    }
    </div>
    { messageModalReplyOpened && <MessageModal
      opened={true}
      message={response}
      thread={thread}
      sendMessageHandler={async () => {
        await threadsService.sendReplyMessage(response, thread)
        setMessageModalReplyOpened(false)
      }}
      discardDraft={() => {
        setMessageModalReplyOpened(false)
      }}
      setShouldEditOnModal={() => {}}
      open={true} onClose={
      () => setMessageModalReplyOpened(false)
    }/> }
  </div>
}
